import { Button, Spinner } from "@heroui/react";
import { GENRE_LABELS } from "@storyline/shared";
import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import PageHeader from "@/src/common/components/page-header";
import Pill from "@/src/common/components/pill";
import SectionCard from "@/src/common/components/section-card";
import { STORY_STATUS_LABELS, STORY_STATUS_TONES } from "@/src/common/constants/studio-labels";
import BibleStudio from "@/src/series/components/bible-studio";
import EpisodesList from "@/src/series/components/episodes-list";
import NewEpisodePanel from "@/src/series/components/new-episode-panel";
import { useSeriesDashboard } from "@/src/series/hooks/use-series-dashboard";

type DashboardTab = "bible" | "episodes";

const SeriesDashboardPage: React.FC = () => {
	const { id = "" } = useParams();
	const navigate = useNavigate();
	const dashboard = useSeriesDashboard(id);
	const [tab, setTab] = useState<DashboardTab>("bible");
	const [addingEpisode, setAddingEpisode] = useState(false);
	const coverInputRef = useRef<HTMLInputElement>(null);

	if (dashboard.isLoading) {
		return (
			<div className="flex justify-center py-16">
				<Spinner size="lg" />
			</div>
		);
	}

	if (dashboard.hasError || !dashboard.detail) {
		return (
			<div className="flex flex-col items-center gap-3 rounded-xl border border-border-soft bg-surface p-10 text-center">
				<p className="text-danger text-sm">Impossible de charger cette série.</p>
				<Button variant="secondary" onPress={() => void dashboard.reload()}>
					Réessayer
				</Button>
			</div>
		);
	}

	const { detail } = dashboard;

	const onCoverSelected = (file: File | undefined): void => {
		if (file) {
			void dashboard.uploadCover(file);
		}
	};

	const onDelete = (): void => {
		if (window.confirm("Supprimer cette série, ses médias et tous ses épisodes ?")) {
			void dashboard.removeSeries().then(() => navigate("/"));
		}
	};

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title={detail.title}
				description={detail.synopsis}
				actions={
					<Button variant="ghost" className="text-danger" onPress={onDelete}>
						Supprimer
					</Button>
				}
			/>

			<div className="flex flex-wrap items-center gap-4 rounded-xl border border-border-soft bg-surface p-4">
				<div className="flex aspect-video w-56 items-center justify-center overflow-hidden rounded-lg bg-glass">
					{detail.coverUrl ? (
						<img src={detail.coverUrl} alt="" className="size-full object-cover" />
					) : (
						<span className="text-muted text-xs">Sans couverture</span>
					)}
				</div>
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-2">
						<Pill tone="accent">{GENRE_LABELS[detail.genre]}</Pill>
						<Pill tone={STORY_STATUS_TONES[detail.status]}>{STORY_STATUS_LABELS[detail.status]}</Pill>
					</div>
					<div className="flex gap-2">
						<Button size="sm" variant="secondary" onPress={() => coverInputRef.current?.click()}>
							Uploader une couverture
						</Button>
						<Button size="sm" variant="ghost" onPress={() => void dashboard.generateCover()}>
							Générer une couverture
						</Button>
					</div>
					<input
						ref={coverInputRef}
						type="file"
						accept="image/*"
						className="hidden"
						onChange={(event) => onCoverSelected(event.target.files?.[0])}
					/>
				</div>
			</div>

			<div className="flex gap-2">
				<Button variant={tab === "bible" ? "secondary" : "ghost"} onPress={() => setTab("bible")}>
					Bible Studio
				</Button>
				<Button variant={tab === "episodes" ? "secondary" : "ghost"} onPress={() => setTab("episodes")}>
					Épisodes ({detail.episodes.length})
				</Button>
			</div>

			{tab === "bible" ? (
				<BibleStudio
					detail={detail}
					assets={dashboard.assets}
					generations={dashboard.generations}
					onSaveBible={dashboard.saveBible}
					onToggleBibleLock={dashboard.toggleBibleLock}
					onAddCharacter={dashboard.addCharacter}
					onEditCharacter={dashboard.editCharacter}
					onRemoveCharacter={dashboard.removeCharacter}
					onAddLocation={dashboard.addLocation}
					onEditLocation={dashboard.editLocation}
					onRemoveLocation={dashboard.removeLocation}
					onGenerate={dashboard.generateAsset}
					onApprove={dashboard.approve}
					onLock={dashboard.lock}
					onUnlock={dashboard.unlock}
				/>
			) : (
				<SectionCard
					title="Épisodes"
					description="Chaque épisode suit son pipeline de production, du copilote à la publication."
					actions={
						<Button size="sm" onPress={() => setAddingEpisode((previous) => !previous)}>
							{addingEpisode ? "Fermer" : "Nouvel épisode"}
						</Button>
					}
				>
					<div className="flex flex-col gap-4">
						{addingEpisode ? (
							<NewEpisodePanel
								seriesId={detail.id}
								characters={detail.characters}
								locations={detail.locations}
								onCreated={(episodeId) => navigate(`/series/${detail.id}/episodes/${episodeId}`)}
								onCancel={() => setAddingEpisode(false)}
							/>
						) : null}
						<EpisodesList seriesId={detail.id} episodes={detail.episodes} />
					</div>
				</SectionCard>
			)}
		</div>
	);
};

export default SeriesDashboardPage;
